import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

interface StageData {
  stage: string;
  count: number;
}

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  appointment_set: "Appointment",
  inspected: "Inspected",
  estimating: "Estimating",
  proposed: "Proposed",
  scheduled: "Scheduled",
  in_progress: "In Progress",
  completed: "Completed",
  closed_lost: "Closed/Lost",
};

export function PipelineChart() {
  const { orgId } = useAuth();
  const [data, setData] = useState<StageData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }

    const fetchPipeline = async () => {
      setLoading(true);
      try {
        const { data: jobs } = await supabase
          .from('jobs')
          .select('status')
          .eq('org_id', orgId);

        const counts: Record<string, number> = {};
        jobs?.forEach(j => {
          const s = j.status || 'new';
          counts[s] = (counts[s] || 0) + 1;
        });

        const chartData = Object.entries(counts)
          .map(([status, count]) => ({
            stage: STATUS_LABELS[status] || status,
            count,
          }))
          .sort((a, b) => b.count - a.count);

        setData(chartData);
      } catch (err) {
        console.error('Error fetching pipeline data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPipeline();
  }, [orgId]);

  return (
    <Card className="backdrop-blur-xl bg-white/10 dark:bg-black/10 border-white/20">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Job Pipeline</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={1}/>
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.6}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              <XAxis dataKey="stage" stroke="hsl(var(--muted-foreground))" fontSize={12} angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [value, "Jobs"]}
              />
              <Bar dataKey="count" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
